import type React from 'react';
import { useState } from 'react';
import { Link } from 'react-router';
import {
  BookCopy,
  BookOpen,
  Cloud,
  Flag,
  FolderOpen,
  GraduationCap,
  LifeBuoy,
  Menu,
  MousePointerClick,
  NotepadText,
  Play,
  Puzzle,
  Server,
  ShieldCheck,
  Sparkles,
  Target,
  TrendingUp,
  UsersRound,
  X,
} from 'lucide-react';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
} from '@/components/ui/navigation-menu';
import NavbarDropdownCard from './NavbarDropdownCard';
import { Button } from './ui/button';

const useCases: { title: string; href: string; description: string; icon: React.ReactNode }[] = [
  {
    title: 'Applicants Assessment',
    href: '/applicants-assessment',
    description: 'Assess your cybersecurity applicants with precision',
    icon: <NotepadText />,
  },
  {
    title: 'Employer Branding',
    href: '/employer-branding',
    description: "Elevate your company's visibility and attract top talent",
    icon: <UsersRound />,
  },
  {
    title: 'Student Training',
    href: '/student-training',
    description: 'Engage students with hands-on cybersecurity simulations',
    icon: <GraduationCap />,
  },
  {
    title: 'Security Team Upskilling',
    href: '/security-team-upskilling',
    description: 'Strengthen security teams through realistic simulations',
    icon: <TrendingUp />,
  },
  {
    title: 'Interactive Product Experiences',
    href: '/interactive-product-experiences',
    description: 'Showcase your product through practical application',
    icon: <MousePointerClick />,
  },
];

const Products: { title: string; href: string; description: string; icon: React.ReactNode }[] = [
  {
    title: 'Simulations Copilot',
    href: '/simulations-copilot',
    description: 'AI-powered simulation recommendation from our library',
    icon: <Sparkles />,
  },
  {
    title: 'Challenge',
    href: '/host-ctf',
    description: 'Host a CTF competition',
    icon: <Flag />,
  },
  {
    title: 'Cyber Range Platform',
    href: '/cyber-range',
    description: 'Create and run real-world cybersecurity simulations',
    icon: <Target />,
  },
  {
    title: 'Scenarios',
    href: '/scenarios',
    description:
      'Explore a library of expert-created scenarios across multiple cybersecurity domains.',
    icon: <Puzzle />,
  },
  {
    title: 'Training Platform',
    href: '/training-platform',
    description: 'Build cybersecurity courses with hands-on labs',
    icon: <BookCopy />,
  },
];

const Resources: {
  title: string;
  href: string;
  target: string;
  description: string;
  icon: React.ReactNode;
}[] = [
  {
    title: 'Product Demo',
    href: '/product-demo',
    target: '_self',
    description: 'Interactive Product Walkthrough',
    icon: <Play />,
  },
  {
    title: 'Blog',
    href: '/blogs',
    target: '_self',
    description: 'News and industry insights',
    icon: <BookOpen />,
  },
  {
    title: 'Case Studies',
    href: '/case-studies',
    target: '_self',
    description: 'Discover how organizations and individuals are thriving with Simulations Labs',
    icon: <FolderOpen />,
  },
  {
    title: 'Guides',
    href: '/guides',
    target: '_self',
    description: 'Best practices for running successful competitions',
    icon: <NotepadText />,
  },
  {
    title: 'Help Center',
    href: 'https://support.simulationslabs.com/support/home',
    target: '_blank',
    description: 'Get started using Simulations Labs',
    icon: <LifeBuoy />,
  },
];

const Deployments: { title: string; href: string; description: string; icon: React.ReactNode }[] = [
  {
    title: 'Software as a Service',
    href: '/deployment#saas',
    description: 'Instant access—no setup required',
    icon: <Cloud />,
  },
  {
    title: 'Private Hosting',
    href: '/deployment#private-hosting',
    description: 'Isolated and secure private instance',
    icon: <ShieldCheck />,
  },
  {
    title: 'On-Premises',
    href: '/deployment#on-premises',
    description: 'Run locally for full control',
    icon: <Server />,
  },
];

export default function Navbar() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <NavigationMenu className="fixed top-0 right-0 left-0 z-50 w-full max-w-full bg-white shadow-md">
      <div className="flex w-full items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex-shrink-0">
          <img src={'/logo.svg'} alt="logo" className="h-8" />
        </Link>

        {/* Mobile menu toggle */}
        <div className="md:hidden">
          <Button variant="ghost" size="icon" onClick={toggleMobileMenu}>
            {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </Button>
        </div>

        {/* Desktop Navigation */}
        <div className="hidden md:flex md:w-full md:items-center md:justify-between">
          {/* Left spacer - matches the width of the right auth buttons */}
          <div className=""></div>

          {/* Centered Navigation */}
          <NavigationMenuList className="mx-auto flex items-center justify-center">
            <NavigationMenuItem>
              <NavigationMenuTrigger>Products</NavigationMenuTrigger>
              <NavigationMenuContent>
                <ul className="grid w-[300px] gap-3 p-4 md:w-[400px]">
                  {Products.map((product) => (
                    <NavbarDropdownCard
                      key={product.title}
                      title={product.title}
                      href={product.href}
                      icon={product.icon}
                      description={product.description}
                    />
                  ))}
                </ul>
              </NavigationMenuContent>
            </NavigationMenuItem>

            <NavigationMenuItem>
              <NavigationMenuTrigger>Use Cases</NavigationMenuTrigger>
              <NavigationMenuContent>
                <ul className="grid w-[400px] gap-3 p-4">
                  {useCases.map((useCase) => (
                    <NavbarDropdownCard
                      key={useCase.title}
                      title={useCase.title}
                      href={useCase.href}
                      icon={useCase.icon}
                      description={useCase.description}
                    />
                  ))}
                </ul>
              </NavigationMenuContent>
            </NavigationMenuItem>

            <NavigationMenuItem>
              <NavigationMenuTrigger>Deployment</NavigationMenuTrigger>
              <NavigationMenuContent>
                <ul className="grid w-[400px] gap-3 p-4">
                  {Deployments.map((deployment) => (
                    <NavbarDropdownCard
                      key={deployment.title}
                      title={deployment.title}
                      href={deployment.href}
                      icon={deployment.icon}
                      description={deployment.description}
                    />
                  ))}
                </ul>
              </NavigationMenuContent>
            </NavigationMenuItem>

            <NavigationMenuItem>
              <NavigationMenuTrigger>Resources</NavigationMenuTrigger>
              <NavigationMenuContent>
                <ul className="grid w-[400px] gap-3 p-4">
                  {Resources.map((resource) => (
                    <NavbarDropdownCard
                      key={resource.title}
                      title={resource.title}
                      href={resource.href}
                      icon={resource.icon}
                      description={resource.description}
                      target={resource.target}
                    />
                  ))}
                </ul>
              </NavigationMenuContent>
            </NavigationMenuItem>

            <NavigationMenuItem>
              <NavigationMenuLink className={navigationMenuTriggerStyle()} asChild>
                <Link to="/pricing">Pricing</Link>
              </NavigationMenuLink>
            </NavigationMenuItem>
          </NavigationMenuList>

          {/* Right auth buttons */}
          <div className="flex justify-end gap-2">
            <Link to="/signin">
              <Button variant="link" size="lg">
                Sign in
              </Button>
            </Link>
            <Button
              variant="default"
              onClick={() => {
                window.location.href = 'https://app.simulationslabs.com/register/tenant';
              }}
            >
              Get Started for Free
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation Menu */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-white px-4 pt-16 md:hidden">
          {/* Close button */}
          <div className="absolute top-4 right-4">
            <Button variant="ghost" size="icon" onClick={toggleMobileMenu} aria-label="Close menu">
              <X className="h-6 w-6" />
            </Button>
          </div>

          <div className="flex flex-col space-y-4">
            <div className="border-b pb-2">
              <div className="py-2 font-medium">Products</div>
              {Products.map((product) => (
                <Link
                  key={product.title}
                  to={product.href}
                  className="flex items-center py-2 pl-4"
                  onClick={toggleMobileMenu}
                >
                  <span className="mr-2">{product.icon}</span>
                  <span>{product.title}</span>
                </Link>
              ))}
            </div>

            <div className="border-b pb-2">
              <div className="py-2 font-medium">Use Cases</div>
              {useCases.map((useCase) => (
                <Link
                  key={useCase.title}
                  to={useCase.href}
                  className="flex items-center py-2 pl-4"
                  onClick={toggleMobileMenu}
                >
                  <span className="mr-2">{useCase.icon}</span>
                  <span>{useCase.title}</span>
                </Link>
              ))}
            </div>

            <Link to="/deployment" className="border-b py-2" onClick={toggleMobileMenu}>
              Deployment
            </Link>

            <div className="border-b pb-2">
              <div className="py-2 font-medium">Resources</div>
              {Resources.map((resource) => (
                <a
                  key={resource.title}
                  href={resource.href}
                  className="flex items-center py-2 pl-4"
                  onClick={toggleMobileMenu}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span className="mr-2">{resource.icon}</span>
                  <span>{resource.title}</span>
                </a>
              ))}
            </div>

            <Link to="/pricing" className="border-b py-2" onClick={toggleMobileMenu}>
              Pricing
            </Link>

            <div className="mt-4 flex flex-col gap-2">
              <Button variant="outline" className="w-full" asChild>
                <Link to="/signin" onClick={toggleMobileMenu}>
                  Sign in
                </Link>
              </Button>
              <Button
                variant="default"
                className="w-full"
                onClick={() => {
                  window.location.href = 'https://app.simulationslabs.com/register/tenant';
                }}
              >
                Get Started for Free
              </Button>
            </div>
          </div>
        </div>
      )}
    </NavigationMenu>
  );
}
